import React from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { useRelativeTime } from '../hooks/useRelativeTime';
import { parseTimestamp } from '../utils/time';

export type FreshnessStatus = 'fresh' | 'stale' | 'failed' | 'updating';

export interface DataFreshnessProps {
  /** When the data was last loaded. A date-time without an offset is read as UTC. */
  updatedAt: string | null;
  status?: FreshnessStatus;
  /** A refresh the reader asked for is running. */
  refreshing?: boolean;
  /** Omit to show freshness only, with no refresh control. */
  onRefresh?: () => void;
  disabled?: boolean;
  refreshLabel?: string;
  staleHint?: string;
  failedHint?: string;
}

/**
 * How current the data on screen is, with the control to refresh it.
 *
 * "Updated 2m ago ⟳", kept current while the page is open. Out-of-date and
 * failed data get a warning colour and icon, so a stale page never passes
 * for a fresh one; a refresh in the background reads "Updating…" rather than
 * a warning, because the reader has nothing to do.
 */
export function DataFreshness({
  updatedAt,
  status = 'fresh',
  refreshing = false,
  onRefresh,
  disabled,
  refreshLabel = 'Refresh data',
  staleHint = 'This data is from before today. Refresh to pull the latest.',
  failedHint = 'The last refresh failed. This is the most recent data we have.',
}: DataFreshnessProps) {
  const age = useRelativeTime(updatedAt);
  const busy = refreshing || status === 'updating';
  const warn = !busy && (status === 'stale' || status === 'failed');

  const text = busy ? 'Updating…' : age ? `Updated ${age}` : 'Not loaded yet';
  const exact = parseTimestamp(updatedAt)?.toLocaleString();
  const hint = warn
    ? status === 'failed' ? failedHint : staleHint
    : exact ? `Last updated ${exact}` : '';

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
      <Tooltip title={hint}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={(t) => ({
            color: warn
              ? status === 'failed' ? t.tokens.semantic.danger : t.tokens.semantic.warning
              : t.tokens.text.secondary,
          })}
        >
          {warn && <WarningAmberRounded fontSize="small" />}
          <Typography variant="caption" noWrap sx={{ color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
            {text}
          </Typography>
        </Stack>
      </Tooltip>

      {onRefresh && (
        <Tooltip title={refreshLabel}>
          <span>
            <IconButton size="small" aria-label={refreshLabel} onClick={onRefresh} disabled={disabled || refreshing}>
              <RefreshRounded
                fontSize="small"
                sx={{
                  animation: refreshing ? 'kit-freshness-spin 1s linear infinite' : 'none',
                  '@keyframes kit-freshness-spin': { to: { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}
